import React, { FC } from 'react'

import { cx } from '@linaria/core'
import { useLocation } from 'react-router'

import { WithClassName, WithDataName } from 'common/types'
import { MobileAvatar } from 'components/designSystem/Avatar/MobileAvatar'
import { CardsSvg } from 'components/designSystem/svgr/CardsSvg'
import { MagnifierSvg } from 'components/designSystem/svgr/MagnifierSvg'
import { ratingPath } from 'components/page/RatingWave/Rating.paths'
import { searchPath } from 'components/page/Search/Search.paths'
import { profilePath } from 'components/paths'
import { useHasUserRestrictions } from 'functions/hasUserRestrictions'
import { mergeAllUrls } from 'functions/mergeAllUrls'
import { GQLGender } from 'generated-graphql/graphql'
import { useCurrentUserRouteMatch } from 'hooks/useCurrentUserRouteMatch'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import {
  activeAvatarCss,
  activeCss,
  Body,
  changeOrderCss,
  defaultCss,
  iconSizeCss,
  Link,
  Parent,
  StyledAvatar,
} from './TabBar.styled'
import { EdnaCounterMessage } from '../Element/EdnaCounterMessage'
import { EventList } from '../Element/EventList'
import { Messenger } from '../Element/Messenger'
import { isActiveTab } from '../function/isActiveTab'

export const TabBarAuthorized: FC<
  WithClassName & WithDataName & { isMobileBar: boolean }
> = ({ className, isMobileBar, ...rest }) => {
  const { pathname } = useLocation()

  const { id, smallPhoto, gender } = useShallowEqualSelector(
    ({
      authorizationReducer: {
        profile: { id },
      },
      userReducer: { smallPhoto, gender },
    }) => ({
      id,
      smallPhoto,
      gender,
    })
  )

  const userHasRestrictions = useHasUserRestrictions()

  const isCurrentUserPath = useCurrentUserRouteMatch(pathname)

  if (userHasRestrictions) {
    return <div style={{ display: 'none' }} />
  }

  return (
    <Parent className={cx(className)} data-name={rest['data-name']}>
      <Link
        to={mergeAllUrls(profilePath, id!)}
        className={cx(
          defaultCss,
          isMobileBar && changeOrderCss,
          isMobileBar && isCurrentUserPath && activeCss
        )}
        data-name="profile-action"
      >
        <Body>
          {!isMobileBar && (
            <EdnaCounterMessage isDesktop={true}>
              <StyledAvatar
                imageSource={smallPhoto}
                className={cx(isCurrentUserPath && activeAvatarCss)}
              />
            </EdnaCounterMessage>
          )}

          {isMobileBar && (
            <EdnaCounterMessage>
              <MobileAvatar gender={gender as GQLGender} size={24} />
            </EdnaCounterMessage>
          )}
        </Body>
      </Link>

      <Link
        to={searchPath}
        className={cx(
          defaultCss,
          isActiveTab(pathname, searchPath) && activeCss
        )}
        data-name="link-main-menu__search-action"
      >
        <Body>
          <MagnifierSvg className={cx(iconSizeCss)} />
        </Body>
      </Link>

      <EventList />

      <Link
        to={ratingPath}
        className={cx(
          defaultCss,
          isActiveTab(pathname, ratingPath) && activeCss
        )}
        data-name="link-main-menu__rating-action"
      >
        <Body>
          <CardsSvg className={cx(iconSizeCss)} />
        </Body>
      </Link>

      <Messenger />
    </Parent>
  )
}
